import React from 'react'
import { Box, Stack } from '@mui/material'
import ReportMotiveCard from '../../components/ReportMotiveCard'
import ReportPayment from '../../components/ReportPayment'
import { ReportTabs } from '../helpers/reportTabs'
import { getKpiCardSubtitle, TOTALS_SECTION_TITLE } from '../helpers/cardCopy'
import {
  buildReportMotiveConfigs,
  buildSummaryCards,
  buildTraceabilityCards,
  KPI_SECTION_CARD_SX,
  KPI_SUMMARY_ICON_SX,
  KPI_SUMMARY_TITLE_SX
} from '../helpers/kpiCardsSection'
import { formatKpiValue, formatKpiValueTitle, isRatioValue } from '../helpers/kpiValueFormatter'
import SectionCard from '../../../layout/SectionCard'
import ReportKpiCard from './ReportKpiCard'
import ReportTraceabilityCard from './ReportTraceabilityCard'
import KpiCollapsibleSection from './KpiCollapsibleSection'

function renderValueNode(valueText, valueConfig, { allowRatioWrap = true } = {}) {
  if (!isRatioValue(valueConfig) || typeof valueText !== 'string' || !valueText.includes('/')) {
    return valueText
  }

  if (!allowRatioWrap) {
    return valueText
  }

  const [numerator, denominator] = valueText.split('/')

  return (
    <>
      <span style={{ whiteSpace: 'nowrap', font: 'inherit', color: 'inherit' }}>{numerator}</span>
      /<wbr />
      <span style={{ whiteSpace: 'nowrap', font: 'inherit', color: 'inherit' }}>{denominator}</span>
    </>
  )
}

function buildSummaryValueSx(card, isDesktopSummary) {
  if (!isDesktopSummary) {
    return card.wide ? { fontSize: { xs: 26, md: 40 } } : {}
  }

  const isRatioCard = isRatioValue(card.value)

  return {
    fontSize: isRatioCard ? { xs: 28, md: 32 } : { xs: 28, md: 34 },
    whiteSpace: 'nowrap',
    overflowWrap: 'normal',
    wordBreak: 'normal'
  }
}

const KpiCardsSection = ({
  kpiData,
  selectedTab,
  collapsibleSections = false,
  compactValues = false,
  isSingleDayRange = true
}) => {
  const normalizedKpi = Array.isArray(kpiData) ? (kpiData[0] ?? {}) : kpiData == null ? {} : kpiData

  const {
    effectiveness_percentage = 0,
    active_users_count = 0,
    geofence_effective_clients_count = 0,
    geofence_location_available_count = 0,
    pos_task_activity_done_task_codes_count = 0,
    pos_task_activity_total_clients_count = 0,
    settlement_done = 0,
    settlement_total = 0,
    total_refund = 0,
    total_order = 0,
    total_quote = 0,
    total_charge = 0,
    total_delivery = 0,
    no_visit_report_motives_order = {},
    no_visit_report_motives_refund = {},
    no_visit_report_motives_charge = {},
    no_visit_report_motives_delivery = {},
    deliveried_documents_percentage = 0,
    charge_documents_percentage = 0
  } = normalizedKpi

  const summaryCards = buildSummaryCards({
    selectedTab,
    effectivenessPercentage: effectiveness_percentage,
    activeUsersCount: active_users_count,
    geofenceEffectiveClientsCount: geofence_effective_clients_count,
    geofenceLocationAvailableCount: geofence_location_available_count,
    settlementDone: settlement_done,
    settlementTotal: settlement_total,
    posTaskDoneCount: pos_task_activity_done_task_codes_count,
    posTaskTotalCount: pos_task_activity_total_clients_count,
    chargeDocumentsPercentage: charge_documents_percentage,
    deliveriedDocumentsPercentage: deliveried_documents_percentage,
    isSingleDayRange
  })

  const paymentMethods = Object.entries(normalizedKpi.payment_methods_summary || {}).map(
    ([text, value]) => ({
      text,
      value
    })
  )
  const showPayment =
    paymentMethods.length > 0 && [ReportTabs.summary, ReportTabs.charges].includes(selectedTab)

  const motiveCardConfigs = buildReportMotiveConfigs({
    selectedTab,
    noVisitReportMotivesOrder: no_visit_report_motives_order,
    noVisitReportMotivesRefund: no_visit_report_motives_refund,
    noVisitReportMotivesCharge: no_visit_report_motives_charge,
    noVisitReportMotivesDelivery: no_visit_report_motives_delivery
  })
    .map(({ title, data }) => {
      const motives = Object.entries(data || {}).map(([text, value]) => ({ text, value }))
      if (motives.length === 0) return null
      return { title, motives }
    })
    .filter(Boolean)

  const motivesAndChartsCount = motiveCardConfigs.length + (showPayment ? 1 : 0)
  const useCompactChartsGrid = motivesAndChartsCount > 0 && motivesAndChartsCount < 3

  const reportMotiveCards = motiveCardConfigs.map(({ title, motives }) => (
    <ReportMotiveCard
      key={title}
      reportMotives={motives}
      title={title}
      selectedTab={selectedTab}
      compact={useCompactChartsGrid}
    />
  ))

  const traceabilityCards = buildTraceabilityCards({
    selectedTab,
    totalOrder: total_order,
    totalQuote: total_quote,
    totalRefund: total_refund,
    totalCharge: total_charge,
    totalDelivery: total_delivery
  })
  const hasMotivesAndCharts = motiveCardConfigs.length > 0 || showPayment
  const motivesAndChartsGridTemplate = useCompactChartsGrid
    ? 'repeat(auto-fit, minmax(min(100%, 400px), 440px))'
    : 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))'
  const isDesktopSummary = !collapsibleSections
  const shouldCompactDisplayValues = compactValues || isDesktopSummary
  const summaryCardViewModels = summaryCards.map((card) => ({
    ...card,
    valueText: formatKpiValue(card.value, { compact: shouldCompactDisplayValues }),
    valueTitle: formatKpiValueTitle(card.value)
  }))

  const summaryInner = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, minmax(0, 1fr))',
          lg: isDesktopSummary
            ? 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))'
            : 'repeat(2, minmax(0, 1fr))'
        },
        gap: 2,
        alignItems: 'stretch'
      }}
    >
      {summaryCardViewModels.map((card) => (
        <Box key={card.key} sx={{ minWidth: 0 }}>
          <ReportKpiCard
            title={card.title}
            subtitle={getKpiCardSubtitle({ cardKey: card.key, title: card.title })}
            value={renderValueNode(card.valueText, card.value, {
              allowRatioWrap: !isDesktopSummary
            })}
            valueTitle={card.valueTitle}
            icon={card.icon}
            titleSx={KPI_SUMMARY_TITLE_SX}
            iconSx={KPI_SUMMARY_ICON_SX}
            valueSx={buildSummaryValueSx(card, isDesktopSummary)}
          />
        </Box>
      ))}
    </Box>
  )

  const motivesInner = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: motivesAndChartsGridTemplate
        },
        gap: { xs: 2, md: 3 },
        alignItems: 'stretch',
        justifyContent: useCompactChartsGrid ? 'flex-start' : 'stretch'
      }}
    >
      {reportMotiveCards.map((card, index) => (
        <Box key={card.key || index} sx={{ minWidth: 0 }}>
          {card}
        </Box>
      ))}

      {showPayment && (
        <Box sx={{ minWidth: 0 }}>
          <ReportPayment
            reportPayment={paymentMethods}
            selectedTab={selectedTab}
            displayTab={[ReportTabs.summary, ReportTabs.charges]}
            compact={useCompactChartsGrid}
          />
        </Box>
      )}
    </Box>
  )

  const totalsInner = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(auto-fit, minmax(min(100%, 250px), 1fr))'
        },
        gap: 2,
        alignItems: 'stretch'
      }}
    >
      {traceabilityCards.map((card) => (
        <ReportTraceabilityCard
          key={card.key}
          title={card.title}
          value={renderValueNode(formatKpiValue(card.value, { compact: false }), card.value)}
          valueTitle={formatKpiValueTitle(card.value)}
          icon={card.icon}
        />
      ))}
    </Box>
  )

  if (collapsibleSections) {
    return (
      <Box sx={{ mb: 2 }}>
        <Stack spacing={2}>
          <KpiCollapsibleSection title="RESUMEN" defaultExpanded>
            {summaryInner}
          </KpiCollapsibleSection>
          {hasMotivesAndCharts ? (
            <KpiCollapsibleSection title="MOTIVOS Y GRÁFICAS">{motivesInner}</KpiCollapsibleSection>
          ) : null}
          {traceabilityCards.length > 0 ? (
            <KpiCollapsibleSection title={TOTALS_SECTION_TITLE}>
              {totalsInner}
            </KpiCollapsibleSection>
          ) : null}
        </Stack>
      </Box>
    )
  }

  return (
    <Box sx={{ mb: 4 }}>
      <SectionCard title="RESUMEN" sx={KPI_SECTION_CARD_SX}>
        {summaryInner}
      </SectionCard>

      {hasMotivesAndCharts && (
        <div className="mt-30">
          <SectionCard title="MOTIVOS Y GRÁFICAS" sx={KPI_SECTION_CARD_SX}>
            {motivesInner}
          </SectionCard>
        </div>
      )}

      {traceabilityCards.length > 0 && (
        <div className="mt-30">
          <SectionCard title={TOTALS_SECTION_TITLE} sx={KPI_SECTION_CARD_SX}>
            {totalsInner}
          </SectionCard>
        </div>
      )}
    </Box>
  )
}

export default KpiCardsSection
